import { useEffect, useState } from 'react';
import {
  AgxCheckbox,
  AgxColumn,
  AgxCurrency,
  AgxTextInput,
  DisplayPriceModel,
  isRuralProperty,
  SaleType,
} from '@urbanx/agx-ui-components';
import './DisplayPrice.scss';
import useFormPropertyDetailsSelector from 'selectors/useFormPropertyDetailsSelector';
import { useAppSelector } from 'hooks/useAppSelector';

const toBeConfirmedLabel = 'To be confirmed';

interface DisplayPriceProps {
  id: string;
  label: string;
  subLabel?: string | null;
  nonRuralAgreementSubLabel?: string | null;
  subDescription?: string | null;
  onValueChanged: (value: {
    id: string;
    value: DisplayPriceModel | null;
  }) => void;
  defaultValue: DisplayPriceModel | null;
  showToBeConfirmed: boolean | null;
  showReservePrice: boolean | null;
  validate: boolean;
  required?: boolean;
}

const DisplayPrice = ({
  id,
  label,
  subLabel,
  nonRuralAgreementSubLabel,
  subDescription,
  onValueChanged,
  defaultValue = null,
  showToBeConfirmed = false,
  showReservePrice = false,
  validate,
  required = false,
}: DisplayPriceProps) => {
  const propertyDetails = useFormPropertyDetailsSelector();

  const saleDetails = useAppSelector(
    state => state.form?.formValues?.SaleDetails
  );

  const isRural = isRuralProperty(propertyDetails.landAreaDetails);

  const [toBeConfirmed, setToBeConfirmed] = useState(
    defaultValue?.toBeConfirmed ?? false
  );

  useEffect(() => {
    onValueChanged({
      id,
      value: {
        toBeConfirmed,
        price: defaultValue?.price ?? null
      },
    });
  }, [toBeConfirmed]);

  const onTextChanged = ({ value }: { value: string | null }) => {
    if (!toBeConfirmed) {
      onValueChanged({
        id,
        value: {
          toBeConfirmed,
          price: value,
        },
      });
    }
  };

  const onCheckboxChanged = ({ value }: { value: boolean }) => {
    setToBeConfirmed(value);
  };

  return (
    <AgxColumn fill largeGap>
      <AgxColumn fill mediumGap>
        {!toBeConfirmed &&
        saleDetails?.saleType !== SaleType.Auction && saleDetails?.saleType !== SaleType.PrivateTreaty &&
        showReservePrice ? (
          <AgxCurrency
            id={id}
            label="Reserve Price"
            onInputValueChange={onTextChanged}
            stretch
            disabled={toBeConfirmed}
            required={required}
            defaultValue={
              toBeConfirmed ? toBeConfirmedLabel : (defaultValue?.price ?? undefined)
            }
            subDescription={subDescription}
            validate={validate}
            noOptionalLabel
            displayErrors={validate && required}
            error={
              validate && required && !defaultValue?.price
                ? 'Enter reserve price'
                : undefined
            }
          />
        ) : (
          <AgxTextInput
            key={toBeConfirmed
              ? "toBeConfirmed"
              : "displayPrice"}
            extraClasses={subLabel ? 'extraGapForSubLabel' : undefined}
            id={id}
            label={
              (isRural &&
                saleDetails?.saleType === SaleType.PrivateTreaty &&
                label) ||
              (isRural &&
                saleDetails?.saleType === SaleType.Auction &&
                'Reserve Price') ||
              (!isRural &&
                saleDetails?.saleType === SaleType.PrivateTreaty &&
                label) ||
              (!isRural &&
                toBeConfirmed &&
                saleDetails?.saleType === SaleType.Auction &&
                'Reserve Price') ||
              (!isRural &&
                (saleDetails?.saleType === SaleType.Auction || saleDetails?.saleType === SaleType.PrivateTreaty) &&
                'Display Price') ||
              label
            }
            onInputValueChange={onTextChanged}
            stretch
            disabled={toBeConfirmed}
            noOptionalLabel
            required={required}
            defaultValue={
              toBeConfirmed ? toBeConfirmedLabel : (defaultValue?.price ?? undefined)
            }
            subDescription={subDescription ?? undefined}
            subLabel={
              !isRural
                ? (nonRuralAgreementSubLabel ?? undefined)
                : (subLabel ?? undefined)
            }
            error={
              validate && required && !defaultValue?.price ? 'Enter display price' : ''
            }
          />
        )}
        {showToBeConfirmed && (
          <AgxCheckbox
            id={`${id}Chkbox`}
            onValueChanged={onCheckboxChanged}
            subLabel={`"${toBeConfirmedLabel}"`}
            stretch
            naked
            defaultValue={toBeConfirmed}
          />
        )}
      </AgxColumn>
    </AgxColumn>
  );
};

export default DisplayPrice;
